import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import invoiceService from '@api/v1/invoiceService'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'
import LoadingSpinner from '@components/common/LoadingSpinner'

/**
 * CC-53458 — the section-level unresolved-read surface for this page.
 *
 * Deliberately a local component rather than a shared one: the two hosts are both in
 * this file, and this page's sections are plain cards rather than a `DataGrid` (which
 * has its own `unresolvedMessage` path). Extracting it would add an import for two
 * call sites in one file.
 *
 * `role="alert"` plus a sentence that DENIES the empty reading, plus a retry — the
 * shape the CC-53132 pass settled on across 41 files. The denial is the load-bearing
 * part: without it a user reads "something went wrong" and still believes the list
 * they can see.
 */
const SectionUnreadable = ({ state, error, onRetry, what, denial }) => (
  <div className="bg-white rounded-xl shadow-sm border border-amber-200 p-8" role="alert">
    <p className="text-sm font-medium text-amber-900">
      {state === 'blocked'
        ? `Can’t reach the server, so ${what} could not be read.`
        : `${what} could not be read.`}
    </p>
    <p className="text-sm text-gray-600 mt-1">{denial}</p>
    {/* cb-api's own words when it sent any (spec 149); axios's when nothing answered. */}
    {(error?.response?.data?.error || error?.message) && (
      <p className="text-xs text-gray-500 mt-2">{error?.response?.data?.error || error.message}</p>
    )}
    <button
      type="button"
      onClick={onRetry}
      className="mt-3 rounded-lg border border-rc-control bg-white px-3 py-1.5 text-xs font-medium text-cb-accent-text hover:bg-gray-50"
    >
      Try again
    </button>
  </div>
)

/**
 * Spec 144 — the invoice itself could not be shown, and the reason decides the words.
 *
 * cb-api answers 404 only when the id names no invoice: it is not a GUID, or CB's
 * model carries nothing from a stored invoice. Everything else (Centerbase failing,
 * answering for a different invoice, the browser being offline) says nothing about
 * whether the invoice exists, so it must not read as "not found". Neither may look
 * like an invoice. Before spec 144 an unknown id, and a paused read, both rendered a
 * blank one: "Invoice —" at $0.00 with a Delete button.
 */
const InvoiceUnreadable = ({ state, error, onRetry }) => {
  const notFound = state === 'failed' && error?.response?.status === 404
  // cb-api's own words when it sent any ("Centerbase did not return this invoice…");
  // axios's message ("Network Error", a timeout) when nothing answered.
  const detail = error?.response?.data?.error || error?.message
  return (
    <div className="max-w-4xl mx-auto">
      <Link to="/invoices" className="text-sm text-cb-accent-text hover:text-cb-navy mb-4 inline-block">&larr; Back to Invoices</Link>
      {notFound ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
          <h1 className="text-xl font-display text-gray-900">Invoice not found</h1>
          <p className="text-sm text-gray-600 mt-1">
            No invoice was found with this id. It may have been deleted, or the link may be wrong.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-amber-200 p-8" role="alert">
          <p className="text-sm font-medium text-amber-900">
            {state === 'blocked'
              ? 'Can’t reach the server, so this invoice could not be read.'
              : 'This invoice could not be read.'}
          </p>
          <p className="text-sm text-gray-600 mt-1">
            Nothing on it was read, so this is not a blank invoice — its figures are unknown, not zero.
          </p>
          {state === 'failed' && detail && (
            <p className="text-xs text-gray-500 mt-2">{detail}</p>
          )}
          <button
            type="button"
            onClick={onRetry}
            className="mt-3 rounded-lg border border-rc-control bg-white px-3 py-1.5 text-xs font-medium text-cb-accent-text hover:bg-gray-50"
          >
            Try again
          </button>
        </div>
      )}
    </div>
  )
}

const InvoiceDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  useDocumentTitle('Invoice')
  const [activeTab, setActiveTab] = useState('line-items')

  const invoiceQuery = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => invoiceService.getInvoice(id),
    enabled: !!id,
  })
  // Spec 144: the invoice read gets the same three-outcome treatment as its sections
  // below (CC-53458). It destructured `isLoading` and `error`, and both are false for
  // a PAUSED read, so an offline browser rendered a blank invoice.
  const invoiceState = useReadState('invoice', invoiceQuery)

  /*
   * CC-53458. These two used to destructure only `data` and `isLoading` — no `error`
   * — and each section then collapsed the result with `… || []`. So on BOTH failure
   * modes the page rendered a confident empty list:
   *
   *  - a FAILED read: nothing was destructured to notice it, so `data` stayed
   *    `undefined` and `items` became `[]`;
   *  - a PAUSED read: `isLoading` is `isPending && isFetching`, which is FALSE for a
   *    paused query (`networkMode: 'online'`), so the spinner branch was skipped too.
   *
   * The Line Items tab therefore rendered **an invoice with no line items**, with no
   * error anywhere. On the invoice document itself, not a list page — and unlike the
   * rest of the CC-53132 family the missing `error` destructure means an ORDINARY
   * server failure produced it, not just an offline browser.
   *
   * Both are conditionally enabled on `activeTab`, so `idle` is genuinely reachable
   * here and must NOT be folded into loading — that yields a permanent spinner on a
   * tab you have not opened (spec 110 §2.3). `idle` is handled by the render sites
   * simply not existing until their tab is active.
   *
   * The queries are kept whole (not destructured) so `useReadState` can see them and
   * `refetch` is available to the retry.
   */
  const lineItemsQuery = useQuery({
    queryKey: ['invoice-line-items', id],
    queryFn: () => invoiceService.getLineItems(id),
    enabled: !!id && activeTab === 'line-items',
  })
  const lineItemsState = useReadState('invoice-line-items', lineItemsQuery)

  // Spec 149: the Add Entries list is scoped the way CB's own dialog scopes it, to
  // the invoice's period by default, or to every date. The period comes from the
  // invoice read, which carries it as ISO date-times, rather than from CB's
  // GetInvoiceData, which formats it in the server's culture ("2/1/2026").
  const period = invoicePeriod(invoiceQuery.data?.data)
  const [unbilledAllDates, setUnbilledAllDates] = useState(false)
  const unbilledRange = period && !unbilledAllDates ? period : null
  const unbilledQuery = useQuery({
    queryKey: ['invoice-unbilled', id, unbilledRange ? `${unbilledRange.startDate}|${unbilledRange.endDate}` : 'all-dates'],
    queryFn: () => invoiceService.getUnbilledEntries(id, unbilledRange || {}),
    enabled: !!id && activeTab === 'unbilled' && invoiceState === 'ready',
  })
  const unbilledState = useReadState('invoice-unbilled', unbilledQuery)

  const deleteMutation = useMutation({
    mutationFn: () => invoiceService.deleteInvoice(id),
    onSuccess: () => navigate('/pre-bills'),
  })

  const invoice = invoiceQuery.data?.data
  // NOT defaulted. `undefined` is the honest value for a read that has not resolved,
  // and every render site below is gated on its section's state.
  const lineItems = lineItemsQuery.data?.data
  const unbilled = unbilledQuery.data?.data

  // `idle` shares the spinner: it needs `enabled: false`, i.e. no `:id`, which this
  // route cannot match. An unhandled state would otherwise render the blank invoice.
  if (invoiceState === 'loading' || invoiceState === 'idle') return <div className="p-12 flex justify-center"><LoadingSpinner message="Loading invoice..." /></div>

  if (invoiceState !== 'ready') {
    return <InvoiceUnreadable state={invoiceState} error={invoiceQuery.error} onRetry={() => invoiceQuery.refetch()} />
  }

  // Normalize — CB's InvoiceModel. The lower-case keys read the flat field-mapped
  // shape of cb-api's item/exists fallback, which spec 144 removed (it could not find
  // any invoice); they are harmless and left as they were.
  const inv = invoice || {}
  const h = inv.Header || inv.header || inv
  // Spec 147: the matter is CB's matter display name (`Casename`, the same value
  // the Invoices list shows) and the client is the bill-to company (`CompanyName`).
  // The keys read before were never in CB's model, so both always showed "—".
  const matter = h.Casename || h.matter || h.MatterName || h.Matter || '—'
  const client = h.CompanyName || h.billToContact || h.company || h.ClientName || h.Client || '—'
  const invoiceNum = h.invoiceNumber || h.InvoiceNumber || h.name || h.RefNum || '—'
  const issueDate = h.issueDate || h.IssueDate || h.Date || '—'
  const status = h.status || h.Status || h.PostingStatus || '—'
  const attorney = h.attorney || h.AttorneyName || h.ResponsibleAttorney || '—'
  // Spec 147: every figure is the field CB's InvoiceModel sends
  // (Endpoint/MesaUtilitiesBase/InvoiceModels.cs). These read keys CB has never
  // sent and defaulted to 0, so a $450 invoice showed Fees $0.00:
  //  - Fees is `TimeTotalAmount`: the time line items, plus a flat fee (:1917, :1932);
  //  - Expenses is `ExpenseTotalAmount`, the expense line items (:1918);
  //  - Tax is `Tax`.
  // CB's own e-billing export reads the same two fields as its fees and expenses
  // (ServerEbillingHub.cs:792). Nothing is computed here. A figure CB does not send
  // is undefined and renders "—", never $0.00.
  const total = amount(h.InvoiceTotal)
  const balance = amount(h.Balance)
  const feeTotal = amount(h.TimeTotalAmount)
  const expenseTotal = amount(h.ExpenseTotalAmount)
  const discount = amount(h.DiscountTotal)
  const tax = amount(h.Tax)
  const interest = amount(h.Interest)

  const TABS = [
    { id: 'line-items', label: 'Line Items' },
    { id: 'unbilled', label: 'Add Entries' },
    { id: 'summary', label: 'Summary' },
    { id: 'raw', label: 'Raw Data' },
  ]

  return (
    <div className="max-w-7xl mx-auto">
      {/* text-cb-accent-text, not text-cb-accent (2.64:1 on this page, spec 147). */}
      <Link to="/invoices" className="text-sm text-cb-accent-text hover:text-cb-navy mb-4 inline-block">&larr; Back to Invoices</Link>

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
        <div className="px-6 py-5 flex items-start justify-between">
          <div>
            <h1 className="text-xl font-display text-gray-900">Invoice {invoiceNum}</h1>
            <p className="text-sm text-gray-500 mt-1">{matter} — {client}</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-semibold text-gray-900" data-testid="invoice-total">{money(total)}</p>
            <p className="text-sm text-gray-500">{status}</p>
          </div>
        </div>
        <div className="px-6 pb-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <InfoField label="Issue Date" value={issueDate && issueDate !== '—' ? dayjs(issueDate).format('MMM D, YYYY') : '—'} />
          <InfoField label="Attorney" value={attorney} />
          <InfoField label="Balance" value={money(balance)} testId="invoice-balance" />
          <InfoField label="Fee Arrangement" value={h.FeeArrangement || h.feeArrangement || '—'} />
        </div>
      </div>

      {/* Financial Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
        <SummaryCard label="Fees" value={feeTotal} testId="invoice-card-fees" />
        <SummaryCard label="Expenses" value={expenseTotal} testId="invoice-card-expenses" />
        <SummaryCard label="Discount" value={discount} negative testId="invoice-card-discount" />
        <SummaryCard label="Tax" value={tax} testId="invoice-card-tax" />
        <SummaryCard label="Interest" value={interest} testId="invoice-card-interest" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'line-items' && (
        lineItemsState === 'blocked' || lineItemsState === 'failed' ? (
          <SectionUnreadable
            state={lineItemsState}
            error={lineItemsQuery.error}
            onRetry={() => lineItemsQuery.refetch()}
            what="this invoice’s line items"
            denial="This is not an invoice with no line items — the list never arrived. Do not treat the total below as complete until it loads."
          />
        ) : (
          <LineItemsSection data={lineItems} state={lineItemsState} />
        )
      )}
      {activeTab === 'unbilled' && (
        unbilledState === 'blocked' || unbilledState === 'failed' ? (
          <SectionUnreadable
            state={unbilledState}
            error={unbilledQuery.error}
            onRetry={() => unbilledQuery.refetch()}
            what="the unbilled entries for this matter"
            denial="This is not a matter with nothing left to bill — the list never arrived."
          />
        ) : (
          <UnbilledSection
            data={unbilled}
            state={unbilledState}
            invoiceId={id}
            queryClient={queryClient}
            hasPeriod={!!period}
            allDates={!unbilledRange}
            onAllDatesChange={setUnbilledAllDates}
          />
        )
      )}
      {activeTab === 'summary' && (
        <SummarySection invoice={inv} />
      )}
      {activeTab === 'raw' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <pre className="text-xs text-gray-600 overflow-x-auto max-h-[500px]">
            {JSON.stringify(invoice, null, 2)}
          </pre>
        </div>
      )}

      {/* Actions */}
      <div className="mt-6 flex gap-3">
        <button
          onClick={() => { if (confirm('Delete this invoice?')) deleteMutation.mutate() }}
          // text-red-700: red-600 on red-50 measured 4.36:1, under AA's 4.5 (spec 147).
          className="px-4 py-2 text-sm text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
        >
          Delete Invoice
        </button>
      </div>
    </div>
  )
}

/**
 * Spec 147. A money figure exactly as CB sent it, or `undefined` when CB sent none.
 * Never a default: `?? 0` is how a field CB does not send became "$0.00" on this
 * page.
 */
const amount = (v) => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : undefined
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v)
  return undefined
}
const money = (v) => (v === undefined ? '—' : `$${v.toFixed(2)}`)

/**
 * Spec 149. The day a CB date-time names ("2026-02-25T00:00:00" → "2026-02-25"), or
 * null. CB sends .NET's minimum date, 0001-01-01, for an entry or a period with no
 * date, and that must not render as January 1 of year 1.
 */
const isoDay = (v) => {
  const m = /^(\d{4}-\d{2}-\d{2})(T|$)/.exec(String(v ?? ''))
  return m && m[1] !== '0001-01-01' ? m[1] : null
}
/** The invoice's own period from CB's model, or null when it has none. */
const invoicePeriod = (inv) => {
  const h = inv?.Header || inv?.header || inv || {}
  const startDate = isoDay(h.StartDate)
  const endDate = isoDay(h.EndDate)
  return startDate && endDate && startDate <= endDate ? { startDate, endDate } : null
}
/** A CB date as this page shows dates. A bare day goes through dayjs as LOCAL midnight. */
const dayText = (v) => {
  const day = isoDay(v)
  return day ? dayjs(day).format('MMM D, YYYY') : '—'
}

// CB's billing-entry class: 1 is time, 2 is an expense (BillingEntries.type).
const TIME_ENTRY = 1
const ENTRY_TYPE = { 1: 'Time', 2: 'Expense' }
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'
const isEntryId = (v) => typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v) && v !== EMPTY_GUID

/** Why an add did not go through, in cb-api's words when it sent any. */
const addFailure = (err) => {
  const said = err?.response?.data?.error
  if (typeof said === 'string' && said) return said
  if (err?.response) {
    return `The add was answered with HTTP ${err.response.status} and no reason. Check this invoice’s line items before trying again: the entry may already be on it.`
  }
  return 'No answer came back, so whether the entry was added is not known. Check this invoice’s line items before trying again.'
}

const InfoField = ({ label, value, testId }) => (
  <div>
    <p className="text-xs text-gray-500 uppercase font-semibold">{label}</p>
    <p className="text-sm text-gray-900 mt-0.5" data-testid={testId}>{value || '—'}</p>
  </div>
)

const SummaryCard = ({ label, value, negative, testId }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-4 py-3">
    <p className="text-xs text-gray-500 uppercase font-semibold">{label}</p>
    <p className={`text-lg font-semibold mt-0.5 ${negative && value > 0 ? 'text-red-600' : 'text-gray-900'}`} data-testid={testId}>
      {value === undefined ? '—' : `${negative && value > 0 ? '-' : ''}${money(value)}`}
    </p>
  </div>
)

const LineItemsSection = ({ data, state }) => {
  // `state`, not `loading` (CC-53458). `isLoading` is `isPending && isFetching`, so it
  // is FALSE for a paused query — this branch was skipped and the `|| []` below then
  // rendered an invoice with no line items. `idle` shares the spinner because this
  // section only mounts once its tab is active, so `enabled` is already true by then.
  if (state !== 'ready') {
    return <div className="p-8 flex justify-center"><LoadingSpinner message="Loading line items..." /></div>
  }

  // Data may be array or object with entries. The `|| []` is safe ONLY because the
  // caller has already refused every unresolved state — it now distinguishes "CB sent
  // a shape we did not expect" from "the read did not happen", which it could not do
  // when an unresolved read reached it.
  const items = Array.isArray(data) ? data : (data?.entries || data?.Entries || data?.lineItems || [])

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="text-base font-semibold text-gray-900">Line Items ({items.length})</h2>
      </div>
      {items.length === 0 ? (
        <p className="p-8 text-sm text-gray-500 text-center">No line items</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Timekeeper</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Description</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Code</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Hours</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Rate</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {items.map((item, i) => (
              <tr key={item.Id || item.id || i} className="hover:bg-gray-50/50">
                {/* Spec 149: CB's ISO date-time ("2026-02-25T00:00:00") was printed raw. */}
                <td className="px-5 py-3 text-gray-600">{dayText(item.Date ?? item.EntryDate ?? item.date)}</td>
                {/* Spec 147: CB's line item names its timekeeper by TimekeeperFirstName /
                    TimekeeperLastName, or else Username (the name CB's own printed
                    invoice uses). TimekeeperName was never in its shape. */}
                <td className="px-5 py-3 text-gray-900">
                  {item.TimekeeperName || item.Timekeeper || item.timekeeper
                    || [item.TimekeeperFirstName, item.TimekeeperLastName].filter(Boolean).join(' ')
                    || item.Username || '—'}
                </td>
                <td className="px-5 py-3 text-gray-600 max-w-xs truncate">{item.Description || item.Notes || item.description || '—'}</td>
                <td className="px-5 py-3 text-gray-600">{item.BillingCodeName || item.ProductName || item.billingCode || '—'}</td>
                <td className="px-5 py-3 text-right text-gray-900">{item.Hours ?? item.Quantity ?? '—'}</td>
                {/* Spec 147: CB sends a line item's rate as `Price` (`Rate` was never in
                    its shape, so every rate read "—"), and an amount CB does not send
                    is "—", not $0.00. */}
                <td className="px-5 py-3 text-right text-gray-600">{money(amount(item.Rate ?? item.Price))}</td>
                <td className="px-5 py-3 text-right font-medium text-gray-900">
                  {money(amount(item.Amount ?? item.Total ?? item.BillableValue))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/**
 * Spec 149 — the Add Entries tab: what CB's own Add Existing Entries dialog would
 * offer for this invoice, or why it would offer nothing.
 *
 * It listed nothing for every invoice. cb-api asked CB for the unbilled entries OF
 * this invoice, which is empty by definition, and this read `entries`/`Entries`,
 * keys CB has never sent. cb-api now asks for the invoice's matter, as CB's dialog
 * does, and says which of three things the answer is (cb-api addEntries.js):
 *  - `open`: the matter's unbilled entries, in the invoice's own period (CB's
 *    default) or at any date. Empty is a real answer, and says which scope it is;
 *  - `posted`: CB adds nothing to a posted invoice, so nothing is listed;
 *  - `no-matter`: CB lists these entries by matter, and the invoice has none.
 * The service has already refused any other answer (invoiceService.readUnbilled),
 * and the caller has refused `blocked`/`failed` (CC-53458).
 *
 * "+ Add" names the entry by `ID`, its GUID. It sent `EntryId`, the entry's short
 * name, which CB's save reads as a NEW, blank billing entry. cb-api now makes the
 * checks CB's dialog makes before it saves, and its answer is shown in its words.
 *
 * Hooks first: the spinner is a render return below them, not an early exit.
 */
const UnbilledSection = ({ data, state, invoiceId, queryClient, hasPeriod, allDates, onAllDatesChange }) => {
  const [notice, setNotice] = useState(null)

  const attachMutation = useMutation({
    // 'always': an add reaches cb-api now, or fails where the user can see it. Under
    // the default, an offline add parks unseen and is sent by itself later.
    networkMode: 'always',
    mutationFn: ({ entryId, confirmUnapproved }) => invoiceService.manageEntries(invoiceId, {
      attachUnbilledIds: [{ ID: entryId }],
      ...(confirmUnapproved ? { confirmUnapproved: true } : {}),
    }),
    onMutate: () => setNotice(null),
    onSuccess: () => setNotice({ tone: 'success', text: 'Entry added to this invoice.' }),
    onError: (err, vars) => {
      const answer = err?.response?.data
      // CB's dialog asks before it adds unapproved time, when the user is allowed to.
      if (answer?.code === 'unapproved' && answer.canConfirm === true && !vars.confirmUnapproved) {
        if (window.confirm(answer.error)) attachMutation.mutate({ ...vars, confirmUnapproved: true })
        else setNotice({ tone: 'info', text: 'Nothing was added.' })
        return
      }
      setNotice({ tone: 'error', text: addFailure(err) })
    },
    // After every outcome, not only success: a failed save may still have attached
    // the entry (cb-api says so), and the invoice's totals move with its line items.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice-line-items', invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['invoice-unbilled', invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] })
    },
  })

  if (state !== 'ready') return <div className="p-8 flex justify-center"><LoadingSpinner message="Loading unbilled entries..." /></div>

  const { status, matterName, startDate, endDate, flatFee, lineItems } = data
  if (status === 'posted' || status === 'no-matter') {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8" data-testid={`unbilled-${status}`}>
        <p className="text-sm font-medium text-gray-900">
          {status === 'posted' ? 'This invoice is posted.' : 'This invoice has no matter.'}
        </p>
        <p className="text-sm text-gray-600 mt-1">
          {status === 'posted'
            ? 'Centerbase does not add entries to a posted invoice, so none are listed here.'
            : 'Centerbase lists the entries it can add by matter, so none are listed here. Add entries to this invoice in Centerbase.'}
        </p>
      </div>
    )
  }

  // The scope is the one cb-api says it APPLIED, not the one this page asked for.
  const matter = matterName || 'this invoice’s matter'
  const scope = startDate ? `dated ${dayText(startDate)} – ${dayText(endDate)}, this invoice’s period` : 'at any date'
  const canAdd = (item) => isEntryId(item.ID) && !(flatFee && item.Class === TIME_ENTRY)

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="text-base font-semibold text-gray-900">Unbilled Entries ({lineItems.length})</h2>
        <p className="text-xs text-gray-500 mt-0.5" data-testid="unbilled-scope">
          Unbilled time and expense entries for {matter}, {scope}.
        </p>
        {hasPeriod && (
          <label className="mt-2 inline-flex items-center gap-2 text-xs text-gray-700">
            <input type="checkbox" checked={allDates} onChange={(e) => onAllDatesChange(e.target.checked)} />
            Include entries outside this invoice’s period
          </label>
        )}
        {flatFee && (
          <p className="text-xs text-gray-600 mt-2" data-testid="unbilled-flat-fee">
            This is a flat-fee invoice. Centerbase does not add time entries to one, so only expenses can be added.
          </p>
        )}
      </div>
      <div aria-live="polite">
        {notice && (
          <p
            role={notice.tone === 'error' ? 'alert' : undefined}
            data-testid="add-entries-notice"
            className={`px-5 py-3 border-b border-gray-100 text-sm ${notice.tone === 'error' ? 'text-red-700' : 'text-gray-700'}`}
          >
            {notice.text}
          </p>
        )}
      </div>
      {lineItems.length === 0 ? (
        <p className="p-8 text-sm text-gray-500 text-center" data-testid="unbilled-empty">
          No unbilled entries for {matter}, {scope}.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <th scope="col" className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
              <th scope="col" className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
              <th scope="col" className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Timekeeper</th>
              <th scope="col" className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Description</th>
              <th scope="col" className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Qty</th>
              <th scope="col" className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Rate</th>
              <th scope="col" className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Amount</th>
              <th scope="col" className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {lineItems.map((item, i) => {
              const date = dayText(item.Date)
              const total = money(amount(item.Total))
              const type = ENTRY_TYPE[item.Class] || item.ClassName || '—'
              const adding = attachMutation.isPending && attachMutation.variables?.entryId === item.ID
              return (
                <tr key={item.ID || i} className="hover:bg-gray-50/50" data-testid="unbilled-row">
                  <td className="px-5 py-3 text-gray-600">{date}</td>
                  <td className="px-5 py-3 text-gray-600">{type}</td>
                  {/* CB names the timekeeper by first and last name, or else Username. */}
                  <td className="px-5 py-3 text-gray-900">
                    {[item.TimekeeperFirstName, item.TimekeeperLastName].filter(Boolean).join(' ') || item.Username || '—'}
                  </td>
                  <td className="px-5 py-3 text-gray-600 max-w-xs truncate">{item.Description || '—'}</td>
                  <td className="px-5 py-3 text-right text-gray-900">{amount(item.Quantity) ?? '—'}</td>
                  <td className="px-5 py-3 text-right text-gray-600">{money(amount(item.Price))}</td>
                  <td className="px-5 py-3 text-right font-medium text-gray-900">{total}</td>
                  <td className="px-5 py-3 text-center">
                    {canAdd(item) ? (
                      <button
                        type="button"
                        onClick={() => attachMutation.mutate({ entryId: item.ID })}
                        disabled={attachMutation.isPending}
                        // Every row's button reads "+ Add", so the name says which entry.
                        // It starts with the visible word (WCAG 2.5.3, label in name).
                        aria-label={`${adding ? 'Adding' : 'Add'} the ${type === '—' ? '' : `${type.toLowerCase()} `}entry ${date === '—' ? 'with no date' : `dated ${date}`}, ${total}, to this invoice`}
                        // text-cb-accent-text, not text-cb-accent (2.76:1 on white).
                        className="text-xs font-medium text-cb-accent-text hover:text-cb-navy disabled:text-gray-500 disabled:cursor-not-allowed"
                      >
                        {adding ? 'Adding…' : '+ Add'}
                      </button>
                    ) : (
                      <span className="text-xs text-gray-500">{flatFee && item.Class === TIME_ENTRY ? 'Flat fee' : '—'}</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}

const SummarySection = ({ invoice }) => {
  const inv = invoice || {}
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <h2 className="text-base font-semibold text-gray-900 mb-4">Invoice Summary</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {Object.entries(inv).map(([key, val]) => {
          if (typeof val === 'object' && val !== null) return null
          return (
            <div key={key} className="flex items-center justify-between py-1.5 border-b border-gray-50">
              <span className="text-sm text-gray-600">{key}</span>
              <span className="text-sm text-gray-900 font-medium">{String(val)}</span>
            </div>
          )
        }).filter(Boolean)}
      </div>
    </div>
  )
}

export default InvoiceDetail
